import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar, HeadingText, ActionMenu } from '@spotoninc/nexus-react';
import {
  useSubscriptionStatus,
  MANAGE_SUBSCRIPTION_PATH,
} from '../subscription/useSubscriptionStatus';
import {
  LogoffIcon,
  LoyaltyBrandIcon,
  QuestionCircleIcon,
  FeedbackIcon,
  CalendarIcon,
  MenuIcon,
} from '@spotoninc/nexus-web-assets';
import { Flex, Box } from '@chakra-ui/react';
import { connect } from 'react-redux';
import { setDateFormat } from '../../redux/slices/dateSlice';
import { clearUserInfo } from '../../redux/slices/userSlice';
import ReskinnedSidebar from '../sidebar/ReskinnedSidebar';
import { useIdentifyUser } from '../../launchdarkly/userIdentification';
import apiEndpoint from '../config/data';
import {
  removeEventProperty,
  resetUserIdentity,
  AnalyticsPropertyNames,
} from '../../utils/analytics';

/**
 * New top navbar component based on the Nexus design system
 * This component is only rendered when the showReskin feature flag is enabled
 */
const ReskinnedNavbar = ({ view, dispatch, onClick, user }) => {
  // State to track sidebar visibility
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Use our custom hook to handle LaunchDarkly user identification
  const { resetIdentification } = useIdentifyUser(user);

  const navigate = useNavigate();

  // "Subscription & Billing" menu item (PROF-1190): self-serve cohort only,
  // gated behind the trial-expiring feature flag (surfaced via the hook).
  const { isEnabled: trialBannerEnabled, isSelfServe } =
    useSubscriptionStatus();
  const showSubscriptionMenuItem = trialBannerEnabled && isSelfServe;

  // Function to toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarOpen(prevState => !prevState);
  };

  // Effect to handle responsive behavior
  useEffect(() => {
    // Close sidebar when window is resized to desktop size
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // User menu options
  const userMenuOptions = [
    {
      label: 'Help & Support',
      onClick: () => {
        window.open('https://www.jotform.com/form/232675275492162', '_blank');
      },
      // Divider sits below "Subscription & Billing" when that item is present.
      hasDivider: !showSubscriptionMenuItem,
    },
    // {
    //   label: 'Feedback',
    //   onClick: () => {
    //     window.open('https://www.jotform.com/form/232675275492162', '_blank');
    //   },
    //   hasDivider: true, // Add a divider after this item
    // },
    ...(showSubscriptionMenuItem
      ? [
          {
            label: 'Subscription & Billing',
            onClick: () => navigate(MANAGE_SUBSCRIPTION_PATH),
            hasDivider: true,
          },
        ]
      : []),
    {
      label: 'Logout',
      onClick: async () => {
        try {
          // Call the logout endpoint to clear HttpOnly cookies on the server
          await fetch(`${apiEndpoint}/api/v1/logout/`, {
            method: 'POST',
            credentials: 'include', // Important: sends cookies to backend
          });
        } catch (error) {
          // Continue with logout even if API call fails
          console.error('Logout API call failed:', error);
        }

        // Remove Heap event properties and reset user identity
        removeEventProperty('integrationConnected');
        removeEventProperty(AnalyticsPropertyNames.BLID);
        resetUserIdentity();

        // Reset LaunchDarkly user identification
        resetIdentification();

        // Clear user data from Redux
        dispatch(clearUserInfo());

        // Clear all authentication-related localStorage items
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('currentUserId');
        localStorage.removeItem('auth_type');
        localStorage.removeItem('redirectedToIntegrations');

        // Note: We're keeping other localStorage items like 'theme' intact
        // Redirect to login page
        window.open('/', '_self');
      },
    },
  ];

  // Create a calendar menu for date format selection
  const CalendarMenu = () => (
    <ActionMenu
      options={[
        {
          label: 'MM-dd-yyyy',
          onClick: () => {
            dispatch(setDateFormat('MM-dd-yyyy'));
            updatePreference('MM-dd-yyyy');
          },
        },
        {
          label: 'dd-MM-yyyy',
          onClick: () => {
            dispatch(setDateFormat('dd-MM-yyyy'));
            updatePreference('dd-MM-yyyy');
          },
        },
        {
          label: 'yyyy-MM-dd',
          onClick: () => {
            dispatch(setDateFormat('yyyy-MM-dd'));
            updatePreference('yyyy-MM-dd');
          },
        },
      ]}
      placement="bottom-start"
      offset={[0, 10]} // Add offset to push the menu down
      className="calendar-dropdown" // Add a class for styling
      disablePortal={false} // Use portal to avoid z-index issues
    >
      {({ getRootProps }) => (
        <div {...getRootProps()} className="calendar-icon-wrapper">
          <CalendarIcon
            size="1.5rem"
            color="#FFFFFF"
            className="calendar-icon"
          />
        </div>
      )}
    </ActionMenu>
  );

  // Function to update user preference for date format
  const updatePreference = format => {
    fetch('/api/fetch_preference/', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage['access']
          ? { Authorization: 'Bearer ' + localStorage['access'] }
          : {}),
      },
      body: JSON.stringify({ date_format: format }),
    })
      .then(response => response.json())
      .then(() => {})
      .catch(() => {});
  };

  // Custom application name with icon
  const customAppName = (
    <Flex
      align="center"
      gap="8px"
      pl="0"
      style={{
        marginLeft: 0,
        paddingLeft: 0,
      }}
      className="app-name-container"
    >
      <LoyaltyBrandIcon
        size="2rem"
        color="#FFFFFF"
        className="app-logo"
        style={{ marginRight: '18px' }}
      />
      <HeadingText className="app-name">Profit Assist</HeadingText>
    </Flex>
  );

  return (
    <Box className="layout-container">
      <div className="topbar-wrapper">
        <TopBar
          applicationName={customAppName}
          currentPageName="Profit Assist"
          onToggleSideBar={toggleSidebar} // Add toggle function
          actions={
            <div className="calendar-menu-wrapper">
              <CalendarMenu />
            </div>
          }
          userMenu={{
            avatar: {
              initials:
                user && user.firstName && user.lastName
                  ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
                  : '',
            },
            options: userMenuOptions,
          }}
          className="reskinned-navbar"
          data-testid="reskinned-navbar"
        />
      </div>

      {/* Render the sidebar conditionally based on sidebarOpen state */}
      {sidebarOpen && (
        <Box className="mobile-sidebar-overlay" onClick={toggleSidebar}>
          <Box
            className="mobile-sidebar-container"
            onClick={e => e.stopPropagation()}
          >
            <ReskinnedSidebar
              onClick={value => {
                if (onClick) onClick(value);
                toggleSidebar(); // Close sidebar after selection on mobile
              }}
              view={view}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = state => ({
  user: state.user,
});

export default connect(mapStateToProps)(ReskinnedNavbar);
